import { useState, useEffect } from 'react';

export interface OfflineState {
  isOffline: boolean;
  isOnline: boolean;
  connectionType: 'online' | 'offline' | 'slow';
}

export const useOffline = (): OfflineState => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [connectionType, setConnectionType] = useState<'online' | 'offline' | 'slow'>('online');

  useEffect(() => {
    const updateOnlineStatus = () => {
      const online = navigator.onLine;
      setIsOffline(!online);
      
      if (online) {
        // Check connection quality
        checkConnectionQuality();
      } else {
        setConnectionType('offline');
      }
    };

    const checkConnectionQuality = () => {
      // Use connection API if available
      const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
      
      if (connection) {
        // Check effective connection type
        if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
          setConnectionType('slow');
        } else {
          setConnectionType('online');
        }
      } else {
        // Fallback: assume good connection if online
        setConnectionType('online');
      }
    };

    // Initial check
    updateOnlineStatus();

    // Listen for online/offline events
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    // Listen for connection changes if supported
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    if (connection) {
      connection.addEventListener('change', checkConnectionQuality);
    }

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      if (connection) {
        connection.removeEventListener('change', checkConnectionQuality);
      }
    };
  }, []);

  return {
    isOffline,
    isOnline: !isOffline,
    connectionType
  };
}; 